import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { forkJoin } from 'rxjs';
import { LoginUser } from '../shared/models/login.user.model';
import { PriceHistory } from '../shared/models/price-history.model';
import { ProductMedia } from '../shared/models/product-media.model';
import { ProductsBackend } from '../shared/models/products.model';
import { AccountService } from '../shared/services/account/account.services';
import { ProductsService } from '../shared/services/product/product.services';
import { WaitlistModalComponent } from '../shared/waitlist-modal/waitlist-modal.component';
import { SnackbarComponent } from '../shared/snackbar/snackbar.component';
import { TranslationConstants } from '../shared/services/util/translation.constants';
import { SnackBarTheme } from '../shared/models/snackbar-theme.enum';
import { CategoriesService } from '../shared/services/categories/categories.service';
import { CategoryItem } from '../shared/models/category-item.model';
import BigNumber from 'bignumber.js';
import { ValueConverterService } from '../shared/services/util/value-converter.service';
import { FeaturesStatusService } from '../shared/services/util/features-status.service';
import { FeatureNames } from '../shared/models/feature-names.enum';
import { ConfigReaderService } from '../shared/services/util/config.reader.service';
import { IKairosConfig } from '../shared/models/IKairosConfig';
import { IExternalLinks } from '../shared/models/IExternalLinks';
import { UtilService } from '../shared/services/util/util.service';
import { IKairosBanners } from '../shared/models/IKairosBanners.model';
import { TypeAssociatedProd } from '../shared/models/category-type.enum';
import { environment } from 'src/environments/environment';
import { realStateCategoryId, waterMark } from '../constants/water-mark.constants';

export class IInfoCards{
    public url: string;
    public description: string;
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {

    public isLetterOfCreditEnabled: boolean;
    public kairosConfigs: IKairosConfig;
    public allowFreeShipping: boolean = false;
    public ratingProduct: number;
    public loggedUser: LoginUser;
    public loading: boolean;
    public email: string;
    public customOptions: OwlOptions = {
        dots: true,
        loop: true,
        navSpeed: 700,
        nav: true,
        mouseDrag: true,
        touchDrag: true,
        pullDrag: true,
        items: 1,
        navText: [
            `<span class="material-icons-round" translate="no" [matTooltip]="'home.previous' | translate" matTooltipClass="tooltip-primary">chevron_left</span>`,
            `<span class="material-icons-round" translate="no" [matTooltip]="'home.next' | translate" matTooltipClass="tooltip-primary">chevron_right</span>`
        ]
    };
    public quote: BigNumber;
    public sales: Array<IKairosBanners> = [];
    public infoCards: Array<IInfoCards> = [];
    public isInfoCardsLoaded: boolean = false;
    public bestSellers: Array<ProductsBackend> = [];
    public featuredCategories: Array<CategoryItem> = [];
    public categories: Array<CategoryItem> = [];
    public releases: Array<ProductsBackend> = [];
    public callToggleFavoriteAgain: boolean = false;
    private externalLinks: IExternalLinks = undefined;
    public browserWidth: number = 0;
    public hideAdvertisementPrice: boolean = false;
    public isSocialBannerEnabled: boolean = false;

    /**
     * To check the width of the browser.
     */
    @HostListener('window:resize', ['$event'])
    public onResize() {
        this.browserWidth = window.innerWidth;
    }

    constructor(
        public dialog: MatDialog,
        private readonly accountService: AccountService,
        private readonly productsService: ProductsService,
        private readonly categoriesService: CategoriesService,
        private readonly router: Router,
        private readonly snackbarComponent: SnackbarComponent,
        private readonly featuresStatusService: FeaturesStatusService,
        private readonly translationConstants: TranslationConstants,
        private readonly myConfigReaderService: ConfigReaderService,
        private readonly valueConverter: ValueConverterService // used on HTML file
    ) { }

    public async ngOnInit(): Promise<void> {
        this.featuresStatusService.getFeatureStatus(FeatureNames.LETTER_OF_CREDIT).subscribe(data => {
            this.isLetterOfCreditEnabled = data;
        });

        this.initHome();
    }

    public ngOnChanges(): void{
        this.initHome();
    }

    public checkWaterMark(products: Array<ProductsBackend>) {
        if (environment.waterMark && products.length) {
            products?.forEach((product) => {
                product.waterMark = product.category_id === realStateCategoryId ? waterMark.realState : waterMark.marketplace
            })
        }
        return products;
    }

    public initHome(): void {
        this.loading = true;
        this.browserWidth = window.innerWidth;

        this.accountService.isAuthenticated(true).subscribe(success => {
            let favorites: boolean;
            if (success) {
                favorites = true;
            }

            this.productsService.getBanners().subscribe(banners => {
                this.sales = banners?.filter(banner => !banner.productId || banner.productId === environment.productId) || [];
            })

            forkJoin([
                this.accountService.getUserDetails(),
                this.productsService.searchProduct({ orderBySell: true, limit: 5, favorites }),
                this.productsService.searchProduct({ orderByDate: true, limit: 5, favorites }),
                this.categoriesService.getCategories(),
                this.myConfigReaderService.getAllExternalLinks(),
                this.myConfigReaderService.getKairosConfig()
            ])
                .subscribe(([user, bestSellers, products, categories, externalLinks, kairosConfigs]) => {
                    this.kairosConfigs = kairosConfigs;
                    this.externalLinks = externalLinks;
                    this.categories = categories;
                    this.hideAdvertisementPrice = kairosConfigs?.hideAdvertisementPrice;
                    this.isSocialBannerEnabled = kairosConfigs?.isSocialBannerEnabled;

                    this.allowFreeShipping = this.kairosConfigs?.allowFreeShipping;

                    this.infoCards = [];
                    this.infoCards.push({ url: '/assets/images/payment.svg', description: 'payment' });
                    this.infoCards.push({ url: '/assets/images/security.svg', description: 'security' });
                    this.infoCards.push({ url: '/assets/images/exchange.svg', description: 'exchange' });
                    this.infoCards.push({ url: '/assets/images/delivery.svg', description: 'delivery' });
                    if (this.allowFreeShipping){
                        this.infoCards.push({ url: '/assets/images/shipping.svg', description: 'shipping' });
                    }
                    //isInfoCardsLoaded must to be set after all infocards are loaded, othewise angular will not identify the list was reloaded.
                    this.isInfoCardsLoaded = true;

                    if (user) {
                        this.loggedUser = user;
                        this.email = user.email;
                    }
                    this.bestSellers = this.checkWaterMark(bestSellers) || [];
                    this.releases = this.checkWaterMark(products) || [];
                    this.featuredCategories = categories?.filter(cat => !cat.parent_category_id);
                }).add(() => {
                    this.loading = false

                    const hasProductId: string = this.productsService.getProductIdFromStorage();
                    /**
                     *  If there is a productId in the storage, it means that the user tried to favorite a product before being logged in,
                     *  so we need to perform the favorite action after logging in.
                     */
                    if (hasProductId) {
                        this.toggleFavorite(hasProductId, true);
                    }
                });
        });
    }

    public receiveNewsLetter(): void {
        if (this.loggedUser?.id) {
            this.loading = true;
            this.accountService.changeNewsLetter(true, this.email).subscribe(success => {
                this.snackbarComponent.openSnackBar(this.translationConstants.translate('home.snackbar.newsLetterSuccess'), SnackBarTheme.success);
            }, error => {
                this.snackbarComponent.openSnackBar(this.translationConstants.translate(`home.snackbar.newsLetterError`), SnackBarTheme.error, 4000);
            }).add(() => this.loading = false);
        } else {
            this.router.navigate(['/account/login']);
        }
    }

    public backTop(): void {
        const element = document.querySelector("#header");

        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    public openWailistModal(): void {
        this.dialog.open(WaitlistModalComponent, {
            panelClass: 'customDialog'
        });
    }

    // Return the last product price
    public getPrice(priceHistory?: Array<PriceHistory>): number {
        if (priceHistory) {
            return priceHistory[0].price;
        } else {
            return 0;
        }
    }

    public getOldPrice(priceHistory?: Array<PriceHistory>): number {
        if (priceHistory) {
            return priceHistory[0].price;
        } else {
            return 0;
        }
    }

    public getProductImage(medias: Array<ProductMedia>): string {
        return UtilService.getExhibitionMediaData(medias);
    }

    public goToCategory(category: string): void {
        this.router.navigate([`search/${category}`]);
    }

    public searchAllCategories(): void {
        this.router.navigate([`search-category`]);
    }

    public searchCategory(categoryId: string, categoryName: string = ''): void {
        this.router.navigate([`search-category/${categoryName}`], { queryParams: { categoryId: categoryId } });
    }

    public definingAction(product: ProductsBackend): void {
        if (product.stock_amount === 0 || !this.getPrice(product?.prices)) {
            this.openWailistModal();
        } else {
            this.goToDetails(product.id);
        }
    }
    
    public goToDetails(productId: string): void {
        this.router.navigate([`product-details/${productId}`]);
    }

    public toggleFavorite(productId: string, avoidRemoving: boolean = false): void {
        const productBestSellersIndex: number = this.bestSellers.findIndex(product => product.id === productId);
        const productReleasesIndex: number = this.releases.findIndex(product => product.id === productId);

        let product: ProductsBackend = this.bestSellers[productBestSellersIndex] || this.releases[productReleasesIndex];

        if (!product || (avoidRemoving && (product?.favorites?.length > 0))) {
            return;
        }

        const addFavorite: boolean = (product?.favorites?.length === 0);

        this.productsService.toggleFavoriteProduct(productId, addFavorite, '/home').subscribe((response: boolean) => {
            if (response) {
                if (addFavorite) {
                    if (productBestSellersIndex > -1) {
                        this.bestSellers[productBestSellersIndex].isFavorite = true;
                        this.bestSellers[productBestSellersIndex].favorites.push({});
                    } else {
                        this.releases[productReleasesIndex].isFavorite = true;
                        this.releases[productReleasesIndex].favorites.push({});
                    }
                } else {
                    if (productBestSellersIndex > -1) {
                        this.bestSellers[productBestSellersIndex].isFavorite = false;
                        this.bestSellers[productBestSellersIndex].favorites.pop();
                    } else {
                        this.releases[productReleasesIndex].isFavorite = false;
                        this.releases[productReleasesIndex].favorites.pop();
                    }
                }
            }
        });
    }

    public goToCreditLetter(): void {
        if (this.loggedUser) {
            this.router.navigate(['/credit-conversion/panel']);
        } else {
            this.router.navigate(['/account/login'], { queryParams: { redirectUrl: '/credit-conversion/panel'}});
        }
    }

    public goToUrl(url): void {
        UtilService.goToUrl(url, this.externalLinks, this.router);
    }

    public getPhotoUrl(url: string, urlResponsive?: string): string{
        if(this.browserWidth <= 428 && urlResponsive){
            return urlResponsive;
        }
        return url;
    }

    public isAdvertisement(product: ProductsBackend): boolean {
        let category: CategoryItem = undefined;
        if (product?.values["subcategoryChild"]) {
            category = this.categories?.find(cat => cat.id === product?.values["subcategoryChild"])
        } else if (product?.values["subcategoryParent"]) {
            category = this.categories?.find(cat => cat.id === product?.values["subcategoryParent"])
        } else {
            category = this.categories?.find(cat => cat.id === product?.category_id)
        }

        return category?.type_associated_prod === TypeAssociatedProd.ANNOUNCE;
    }
}
